import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Spinner, Alert, Button, Icon, StatTile } from '../../../core/ui';
import {
  debtForecast,
  getSpending,
  listInstallments,
} from '../api/budget';
import { formatTry } from '../format';
import { endOfMonth, toLocalIso } from '../reports/spendingFormat';
import { useTranslation } from '../../../core/context/TranslationContext';
import styles from './BudgetOverviewWidget.module.css';

export default function BudgetOverviewWidget() {
  const { t } = useTranslation();
  const [thisMonthSpend, setThisMonthSpend] = useState(0);
  const [nextMonthDue, setNextMonthDue] = useState(0);
  const [openInstallmentCount, setOpenInstallmentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const now = new Date();
        const from = toLocalIso(new Date(now.getFullYear(), now.getMonth(), 1));
        const to = toLocalIso(endOfMonth(now));
        const [spending, forecast, installments] = await Promise.all([
          getSpending(from, to),
          debtForecast(2),
          listInstallments('open'),
        ]);
        if (cancelled) return;
        setThisMonthSpend(Number(spending.total || 0));
        setNextMonthDue(forecast.length > 1 ? Number(forecast[1].totalDueTry) : 0);
        setOpenInstallmentCount(installments.length);
      } catch (err) {
        if (!cancelled) setError(err?.message || t('error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (loading) {
    return (
      <Card>
        <Card.Header>
          <Card.Title>{t('budget.dashboardTitle')}</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className={styles.centered}>
            <Spinner label={t('loading')} />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Header>
          <Card.Title>{t('budget.dashboardTitle')}</Card.Title>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t('budget.dashboardTitle')}</Card.Title>
        <Link to="/budget/statements" className={styles.viewAll}>
          {t('viewAll')} →
        </Link>
      </Card.Header>

      <Card.Body>
        <div className={styles.statsGrid}>
          <StatTile
            label={t('budget.widgetMonthSpend')}
            value={formatTry(thisMonthSpend)}
            hint={t('budget.widgetMonthSpendHint')}
          />
          <StatTile
            label={t('budget.widgetNextMonthDue')}
            value={formatTry(nextMonthDue)}
            intent={nextMonthDue > 0 ? 'warning' : 'neutral'}
          />
          <StatTile
            label={t('budget.widgetOpenInstallments')}
            value={openInstallmentCount}
          />
        </div>

        <div className={styles.actions}>
          <Link to="/budget/upload-statement">
            <Button variant="secondary" size="sm">
              <Icon name="fa-upload" size="sm" />
              {t('budget.uploadStatement')}
            </Button>
          </Link>
          <Link to="/budget/statements">
            <Button variant="secondary" size="sm">
              <Icon name="fa-file-alt" size="sm" />
              {t('budget.statements')}
            </Button>
          </Link>
          <Link to="/budget/installments">
            <Button variant="secondary" size="sm">
              <Icon name="fa-calendar" size="sm" />
              {t('budget.installments')}
            </Button>
          </Link>
          <Link to="/reports/budget/spending-by-category">
            <Button variant="secondary" size="sm">
              <Icon name="fa-tags" size="sm" />
              {t('budget.reportSpendingByCategory')}
            </Button>
          </Link>
        </div>
      </Card.Body>
    </Card>
  );
}
